import {
  decodeTurnAudioFrame,
  encodeSttAudioFrame,
  floatToInt16Pcm,
  type RpcReply,
  type ServerEvent,
  type SttAudioMeta,
  type SttResult,
} from '@voicer/shared';

type Listener = (ev: ServerEvent) => void;

function readStoredWsToken(): string {
  if (typeof sessionStorage !== 'undefined') {
    const fromSession = sessionStorage.getItem('voicer_ws_token');
    if (fromSession) return fromSession;
  }
  if (typeof localStorage !== 'undefined') {
    const fromLocal = localStorage.getItem('voicer_ws_token');
    if (fromLocal) return fromLocal;
  }
  return '';
}

function wsUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  const token =
    (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_WS_TOKEN ||
    readStoredWsToken() ||
    '';
  const q = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${proto}://${location.host}/ws${q}`;
}

/** Reconnecting WebSocket with request/response RPC and server push events. */
export class WsClient {
  private socket: WebSocket | null = null;
  private pending = new Map<string, { resolve: (v: unknown) => void; reject: (e: Error) => void; sentAt: number }>();
  private listeners = new Set<Listener>();
  private reqCounter = 0;
  private backoff = 500;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private wantConnected = false;
  connected = false;
  onConnectionChange: ((connected: boolean) => void) | null = null;

  constructor(private urlFactory: () => string = wsUrl) {}

  connect() {
    this.wantConnected = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }
    const ws = new WebSocket(this.urlFactory());
    ws.binaryType = 'arraybuffer';
    this.socket = ws;
    ws.onopen = () => {
      this.connected = true;
      this.backoff = 500;
      this.onConnectionChange?.(true);
    };
    ws.onmessage = (e) => {
      if (typeof e.data !== 'string') {
        const data = decodeTurnAudioFrame(e.data as ArrayBuffer);
        if (!data) return;
        const ev: ServerEvent = { type: 'turn.audio', data };
        for (const l of this.listeners) l(ev);
        return;
      }
      let msg: ServerEvent | RpcReply;
      try {
        msg = JSON.parse(e.data) as ServerEvent | RpcReply;
      } catch {
        return;
      }
      if (msg.type === 'reply') {
        const p = this.pending.get(msg.reqId);
        if (!p) return;
        this.pending.delete(msg.reqId);
        if (msg.ok) p.resolve(msg.data);
        else p.reject(new Error(msg.error ?? 'rpc failed'));
        return;
      }
      for (const l of this.listeners) l(msg);
    };
    ws.onclose = () => {
      this.connected = false;
      this.onConnectionChange?.(false);
      for (const [, p] of this.pending) p.reject(new Error('socket closed'));
      this.pending.clear();
      this.socket = null;
      if (!this.wantConnected) return;
      this.reconnectTimer = setTimeout(() => this.connect(), this.backoff);
      this.backoff = Math.min(8000, this.backoff * 1.7);
    };
    ws.onerror = () => ws.close();
  }

  /** Stop reconnect loops (e.g. while the splash password gate is showing). */
  disconnect() {
    this.wantConnected = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const sock = this.socket;
    this.socket = null;
    if (sock) {
      sock.onclose = null;
      sock.onerror = null;
      sock.onmessage = null;
      sock.onopen = null;
      sock.close();
    }
    this.connected = false;
    this.onConnectionChange?.(false);
    for (const [, p] of this.pending) p.reject(new Error('socket closed'));
    this.pending.clear();
  }

  on(listener: Listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  rpc<T = unknown>(type: string, data: unknown = {}, timeoutMs = 120_000): Promise<T> {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return Promise.reject(new Error('not connected'));
    const reqId = `${++this.reqCounter}_${Date.now().toString(36)}`;
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.pending.delete(reqId)) reject(new Error(`rpc ${type} timed out`));
      }, timeoutMs);
      this.pending.set(reqId, {
        resolve: (v) => {
          clearTimeout(timer);
          resolve(v as T);
        },
        reject: (e) => {
          clearTimeout(timer);
          reject(e);
        },
        sentAt: Date.now(),
      });
      this.socket!.send(JSON.stringify({ type, reqId, data }));
    });
  }

  /** Binary VST1 STT — avoids base64 JSON on the mic hot path. */
  sttBinary(
    audio: Float32Array,
    opts: { lane: 'main' | 'bargein'; sampleRate?: number; prompt?: string; turnId?: string; partial?: boolean },
    timeoutMs = 60_000,
  ): Promise<SttResult> {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return Promise.reject(new Error('not connected'));
    const reqId = `${++this.reqCounter}_${Date.now().toString(36)}`;
    const meta: SttAudioMeta = {
      lane: opts.lane,
      sampleRate: opts.sampleRate ?? 16000,
      prompt: opts.prompt,
      turnId: opts.turnId,
      partial: opts.partial,
      reqId,
    };
    const frame = encodeSttAudioFrame(meta, floatToInt16Pcm(audio));
    return new Promise<SttResult>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.pending.delete(reqId)) reject(new Error('rpc stt timed out'));
      }, timeoutMs);
      this.pending.set(reqId, {
        resolve: (v) => {
          clearTimeout(timer);
          resolve(v as SttResult);
        },
        reject: (e) => {
          clearTimeout(timer);
          reject(e);
        },
        sentAt: Date.now(),
      });
      this.socket!.send(frame);
    });
  }
}

export const ws = new WsClient();
